// Values of one taxonomy (R-C7): rename, colour, order up and down, archive with the reason when an app needs the value, restore from the archive.

import { useState } from 'react';
import { termLock, uuidv7, type Id, type TermDef } from '@confdeck/core-shared';
import { useMeta, useSync } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { IconButton, Button, Chip, Input, Menu, MenuContent, MenuItem, MenuTrigger, useToast } from '../ui/primitives.tsx';
import s from '../table/settings.module.css';
import { ArchiveConfirm } from './ArchiveConfirm.tsx';
import { StageRules } from './StageRules.tsx';

const COLORS = ['gray', 'blue', 'green', 'amber', 'red', 'violet', 'teal', 'pink'];

export function TermsEditor({ taxonomyId, canEdit }: { taxonomyId: Id; canEdit: boolean }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const tax = meta.taxonomy(taxonomyId)!;
  const [adding, setAdding] = useState<{ parent: Id | null; name: string } | null>(null);
  const [renaming, setRenaming] = useState<{ id: Id; name: string } | null>(null);
  const [showArchive, setShowArchive] = useState(false);
  const [archiving, setArchiving] = useState<{ id: Id; name: string } | null>(null);
  const [rules, setRules] = useState<Id | null>(null);
  const roots = tax.style === 'pipeline' ? meta.children(null, tax.id) : [null];
  const archived = meta.archived<TermDef>('term', (t) => t.taxonomyId === tax.id);

  const list = (parent: Id | null) => (parent === null && tax.style !== 'pipeline' ? meta.terms(tax.id) : meta.children(parent, tax.id));
  const run = async (command: string, input: unknown, ok?: string) => {
    const r = await client.run(command, input);
    if (!r.ok) toast({ kind: 'error', text: r.error.message }); else if (ok) toast({ kind: 'info', text: ok });
  };
  const addRow = (parent: Id | null) => adding && adding.parent === parent ? (
    <Input autoFocus value={adding.name} placeholder={tax.style === 'pipeline' ? 'Новый этап' : 'Новое значение'} className={s.smallInput}
      onChange={(e) => setAdding({ parent, name: e.target.value })} onBlur={() => setAdding(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && adding.name.trim()) { client.mutate('term.create', { id: uuidv7(), taxonomyId: tax.id, parentId: parent, name: adding.name.trim() }); setAdding({ parent, name: '' }); }
        if (e.key === 'Escape') setAdding(null);
      }} />
  ) : canEdit && <button type="button" className={s.addTerm} onClick={() => setAdding({ parent, name: '' })}><Icon name="plus" size={13} />{tax.style === 'pipeline' ? 'Этап' : 'Значение'}</button>;

  const row = (t: TermDef, i: number, arr: TermDef[]) => {
    const lock = termLock(meta, t);
    if (renaming?.id === t.id) {
      return (
        <div key={t.id} className={s.term}>
          <Input autoFocus value={renaming.name} className={s.smallInput} aria-label="Название значения" onChange={(e) => setRenaming({ id: t.id, name: e.target.value })} onBlur={() => setRenaming(null)}
            onKeyDown={(e) => { if (e.key === 'Enter' && renaming.name.trim()) { void run('term.update', { id: t.id, name: renaming.name.trim() }); setRenaming(null); } if (e.key === 'Escape') setRenaming(null); }} />
        </div>
      );
    }
    return (
      <div key={t.id} className={s.term}>
        <Menu>
          <MenuTrigger asChild disabled={!canEdit}><button type="button" className={s.colorBtn} aria-label={`Значение ${t.name}`}><Chip color={t.color} dot={tax.style === 'pipeline'}>{t.name}</Chip></button></MenuTrigger>
          <MenuContent>
            <MenuItem icon="text" onSelect={() => setRenaming({ id: t.id, name: t.name })}>Переименовать</MenuItem>
            {tax.style === 'pipeline' && t.parentId && <MenuItem icon="filter" onSelect={() => setRules(t.id)}>Условия перехода…</MenuItem>}
            <div className={s.colors}>{COLORS.map((c) => <button key={c} type="button" className={s.swatch} style={{ background: `var(--c-${c}-dot)` }} onClick={() => client.mutate('term.update', { id: t.id, color: c })} aria-label={c} />)}</div>
            {lock.archive
              ? <MenuItem icon="archive" danger onSelect={() => setArchiving({ id: t.id, name: t.name })}>В архив</MenuItem>
              : <MenuItem icon="lock" disabled>{lock.why}</MenuItem>}
          </MenuContent>
        </Menu>
        {t.final && <span className={s.final}>{t.final === 'won' ? 'успех' : 'отказ'}</span>}
        {(t.rules?.reason || t.rules?.fields?.length) && <span className={s.final} title={[t.rules.reason && 'нужна причина', t.rules.fields?.length && `обязательных полей: ${t.rules.fields.length}`].filter(Boolean).join(', ')}>условия</span>}
        <span className={s.spacer} />
        {/* Up and down between the neighbours (B17): the value takes the middle of the next gap; final stages stay last. */}
        {canEdit && !t.final && i > 0 && <IconButton size="sm" icon="chevronUp" label="Выше" onClick={() => client.mutate('term.update', { id: t.id, order: (arr[i - 1]!.order + (arr[i - 2]?.order ?? arr[i - 1]!.order - 1)) / 2 })} />}
        {canEdit && !t.final && i < arr.length - 1 && !arr[i + 1]!.final && <IconButton size="sm" icon="chevronDown" label="Ниже" onClick={() => client.mutate('term.update', { id: t.id, order: (arr[i + 1]!.order + (arr[i + 2]?.order ?? arr[i + 1]!.order + 1)) / 2 })} />}
      </div>
    );
  };

  return (
    <div className={s.terms}>
      {roots.map((root) => (
        <div key={root?.id ?? 'all'} className={s.termGroup}>
          {root && <div className={s.pipeName}><Icon name="stage" size={13} />{root.name}</div>}
          {list(root?.id ?? null).map(row)}
          {addRow(root?.id ?? null)}
        </div>
      ))}
      {rules && <StageRules termId={rules} onClose={() => setRules(null)} />}
      {archiving && <ArchiveConfirm kind="term" id={archiving.id} label={archiving.name} onConfirm={() => void run('term.archive', { id: archiving.id }, `«${archiving.name}» в архиве. Записи со значением его сохраняют.`)} onClose={() => setArchiving(null)} />}
      {canEdit && archived.length > 0 && (
        <div className={s.termGroup}>
          <button type="button" className={s.addTerm} onClick={() => setShowArchive(!showArchive)}><Icon name="archive" size={13} />Архив: {archived.length}</button>
          {showArchive && archived.map((t) => (
            <div key={t.id} className={s.term}>
              <Chip color={t.color}>{t.name}</Chip><span className={s.spacer} />
              <Button size="sm" variant="ghost" onClick={() => void run('term.restore', { id: t.id }, `«${t.name}» снова в списке`)}>Вернуть</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
